import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import {
  PageViewTracker,
  BASE_PATHS,
  COOKIES_CONFIG,
  PATHS,
} from '@belong/common';
import to from 'await-to-js';
import { fetchHomeService } from 'src/api/homes';
import { fetchResidentApplicationsService } from 'src/api/resident-application';
import ErrorBoundary from 'src/components/error-boundary/error-boundary';
import { DEFAULT_RESIDENT_GROWTH } from 'src/constants/employee-assignment';
import ResidentApplicationGetStarted, {
  GetStartedProps,
} from 'src/features/resident-application/get-started';
import { getResidentApplicationLayout } from 'src/features/resident-application/layout';
import { getStringParam } from 'src/utils/params';
import { buildServerClient } from 'src/utils/server-client';
import { SWRConfig } from 'swr';

type GetStartedPageProps = Pick<
  GetStartedProps,
  | 'homeId'
  | 'residentGrowth'
  | 'homeBasicInfo'
  | 'homeInterests'
  | 'listingInfo'
  | 'currentPayoutPlanType'
> & {
  fallback: Record<string, unknown>;
  /** Consumed by `getResidentApplicationLayout`, not by the page itself. */
  exitHref: string;
};

export default function GetStartedPage({
  fallback,
  exitHref,
  ...rest
}: GetStartedPageProps) {
  const router = useRouter();

  function continueApplication(
    applicationId: string,
    isMainApplicant: boolean,
  ) {
    router.push({
      pathname: isMainApplicant
        ? PATHS.RESIDENT_APPLICATION_LEASE_STEP
        : PATHS.RESIDENT_APPLICATION_PRE_APPLICANT_FLOW,
      query: { applicationId, homeId: rest.homeId },
    });
  }

  function startApplication(applicationId: string) {
    router.push({
      pathname: PATHS.RESIDENT_APPLICATION_LEASE_STEP,
      query: { applicationId, homeId: rest.homeId },
    });
  }

  return (
    <PageViewTracker name="resident_application_get_started_page">
      <ErrorBoundary>
        <SWRConfig value={{ fallback, revalidateOnFocus: false }}>
          <ResidentApplicationGetStarted
            {...rest}
            continueApplication={continueApplication}
            startApplication={startApplication}
          />
        </SWRConfig>
      </ErrorBoundary>
    </PageViewTracker>
  );
}

GetStartedPage.getLayout = getResidentApplicationLayout;

export const getServerSideProps: GetServerSideProps<
  GetStartedPageProps
> = async (ctx) => {
  const homeIdParam = getStringParam(ctx.query?.homeId);

  if (!homeIdParam) {
    console.error(
      'GetStartedPage => getServerSideProps => homeIdParam',
      homeIdParam,
    );

    return {
      redirect: {
        permanent: true,
        destination: BASE_PATHS.HOME,
      },
    };
  }

  const apiClient = buildServerClient(ctx);

  /**
   * This page is public, so the visitor may not be signed in. Matches the check the
   * middleware itself uses to gate `PROTECTED_PATHS`.
   */
  const hasSessionCookie = Boolean(
    ctx.req.cookies[COOKIES_CONFIG.JWT_TOKEN.name],
  );

  const [[homeDetailsError, homeDetails], [applicationsError, applications]] =
    await Promise.all([
      to(fetchHomeService(homeIdParam, apiClient)),
      hasSessionCookie
        ? to(fetchResidentApplicationsService(apiClient))
        : Promise.resolve([null, null]),
    ]);

  if (homeDetailsError) {
    console.error(
      'GetStartedPage => getServerSideProps => homeDetailsError',
      homeDetailsError,
    );
    return {
      redirect: {
        permanent: true,
        destination: BASE_PATHS.HOME,
      },
    };
  }

  if (applicationsError) {
    console.error(
      'GetStartedPage => getServerSideProps => applicationsError',
      applicationsError,
    );
  }

  /**
   * Only send the visitor to their account page on positive evidence that their
   * session works: exiting to `/accounts/*` without one lands them on the
   * protected-route guard (or, with a stale cookie the guard still accepts, on the
   * legacy account page's error fallback, which bounces them to `/support`). A
   * cookie the applications read just rejected is not a session. Everyone else
   * exits to the listing they were applying to.
   */
  const isSignedIn = hasSessionCookie && !applicationsError;

  const props: GetStartedPageProps = {
    homeId: homeIdParam,
    exitHref: isSignedIn
      ? PATHS.RESIDENTS_ACCOUNT_TOURS_AND_APPS
      : `${BASE_PATHS.HOME}/${homeIdParam}`,
    residentGrowth:
      homeDetails?.employees?.residentGrowth || DEFAULT_RESIDENT_GROWTH,
    homeBasicInfo: homeDetails?.basicInfo || null,
    homeInterests: homeDetails?.homeInterests || null,
    listingInfo: homeDetails?.listingInfo || null,
    currentPayoutPlanType: homeDetails?.currentPayoutPlanType ?? undefined,
    fallback: {
      ['/residents/applications']: applications || [],
    },
  };

  return { props };
};
